import type { ClerkPaginationRequest } from '@clerk/shared/types';

import type { PaginatedResourceResponse } from '../../api/resources/Deserializer';
import { joinPaths } from '../../util/path';
import type { APIKey } from '../resources/APIKey';
import type { DeletedObject } from '../resources/DeletedObject';
import { AbstractAPI } from './AbstractApi';

const basePath = '/api_keys';

type GetAPIKeyListParams = ClerkPaginationRequest<{
  /**
   * The user or Organization ID to query API keys by
   */
  subject: string;
  /**
   * Whether to include invalid API keys.
   *
   * @default false
   */
  includeInvalid?: boolean;
}>;

type CreateAPIKeyParams = {
  /**
   * API key name
   */
  name: string;
  /**
   * The user or Organization ID to associate the API key with
   */
  subject: string;
  /**
   * API key description
   */
  description?: string | null;
  claims?: Record<string, any> | null;
  scopes?: string[];
  createdBy?: string | null;
  secondsUntilExpiration?: number | null;
};

type RevokeAPIKeyParams = {
  /**
   * API key ID
   */
  apiKeyId: string;
  /**
   * Reason for revocation
   */
  revocationReason?: string | null;
};

type UpdateAPIKeyParams = {
  /**
   * API key ID
   */
  apiKeyId: string;
  /**
   * The user or Organization ID to associate the API key with
   */
  subject: string;
  /**
   * API key description
   */
  description?: string | null;
  claims?: Record<string, any> | null;
  scopes?: string[];
  secondsUntilExpiration?: number | null;
};

export class APIKeysAPI extends AbstractAPI {
  async list(queryParams: GetAPIKeyListParams) {
    return this.request<PaginatedResourceResponse<APIKey[]>>({
      method: 'GET',
      path: basePath,
      queryParams,
    });
  }

  async create(params: CreateAPIKeyParams) {
    return this.request<APIKey>({
      method: 'POST',
      path: basePath,
      bodyParams: params,
    });
  }

  async get(apiKeyId: string) {
    this.requireId(apiKeyId);

    return this.request<APIKey>({
      method: 'GET',
      path: joinPaths(basePath, apiKeyId),
    });
  }

  async update(params: UpdateAPIKeyParams) {
    const { apiKeyId, ...bodyParams } = params;

    this.requireId(apiKeyId);

    return this.request<APIKey>({
      method: 'PATCH',
      path: joinPaths(basePath, apiKeyId),
      bodyParams,
    });
  }

  async delete(apiKeyId: string) {
    this.requireId(apiKeyId);

    return this.request<DeletedObject>({
      method: 'DELETE',
      path: joinPaths(basePath, apiKeyId),
    });
  }

  async revoke(params: RevokeAPIKeyParams) {
    const { apiKeyId, revocationReason = null } = params;

    this.requireId(apiKeyId);

    return this.request<APIKey>({
      method: 'POST',
      path: joinPaths(basePath, apiKeyId, 'revoke'),
      bodyParams: { revocationReason },
    });
  }

  async getSecret(apiKeyId: string) {
    this.requireId(apiKeyId);

    return this.request<{ secret: string }>({
      method: 'GET',
      path: joinPaths(basePath, apiKeyId, 'secret'),
    });
  }

  async verify(secret: string) {
    return this.request<APIKey>({
      method: 'POST',
      path: joinPaths(basePath, 'verify'),
      bodyParams: { secret },
    });
  }
}
